import { Chance } from 'chance';
// @ts-ignore
import usCities from 'datasets-us-states-capitals';
import * as _ from 'lodash/fp';
import moment from 'moment-timezone';
// @ts-ignore
import romanize from 'romanization';

const chance = new Chance();

const greekAlphabet = [
  'Alpha',
  'Beta',
  'Gamma',
  'Delta',
  'Epsilon',
  'Zeta',
  'Eta',
  'Theta',
  'Iota',
  'Kappa',
  'Lambda',
  'Mu',
  'Nu',
  'Xi',
  'Omicron',
  'Pi',
  'Rho',
  'Sigma',
  'Tau',
  'Upsilon',
  'Phi',
  'Chi',
  'Psi',
  'Omega'
];
const ONE_MINUTE = 1 * 1000 * 60;
const ONE_DAY = ONE_MINUTE * 60 * 24;
const ONE_YEAR = ONE_DAY * 365;

type Department = {
  name: string;
  titles: string[];
  teams: string[];
};

export type GeneratedUser = {
  firstName: string;
  lastName: string;
  gender: 'male' | 'female';
  email: string;
  managerEmail: string;
  department: string;
  position: string;
  team: string;
  city: string;
  employeeId: number;
  hireDate: string; // M/D/YYYY
};

const cities = (): string[] => chance.pickset(usCities, 4);
const companyDomain = () => chance.domain({ tld: 'test' });
const departments = (): Department[] =>
  chance.pickset(
    [
      {
        name: 'Engineering',
        titles: chance.pickset(
          [
            'Software Architect',
            'Backend Developer',
            'Frontend Developer',
            'QA',
            'Team Lead',
            'CTO'
          ],
          3
        ),
        teams: chance.pickset(greekAlphabet, 3)
      },
      {
        name: chance.pickone([
          'Customer Happiness',
          'Customer Success',
          'Customer Support'
        ]),
        titles: chance.pickset(
          [
            'Customer Support Agent',
            'Handler',
            'Customer Success Engineer',
            'Customer Liaison'
          ],
          3
        ),
        teams: chance.pickset(greekAlphabet, 3)
      },
      {
        name: 'HR',
        titles: chance.pickset(
          [
            'Employee Satisfaction Engineer',
            'Manager of Happiness',
            'Headhunter'
          ],
          2
        ),
        teams: chance.pickset(greekAlphabet, 3)
      }
      // {
      //   name: 'Design',
      //   titles: chance.pickset(
      //     ['Designer', 'Graphic Designer', 'UX Designer', 'Manager of Visuals'],
      //     3
      //   ),
      //   teams: chance.pickset(greekAlphabet, 3)
      // },
      // {
      //   name: 'Marketing',
      //   titles: chance.pickset(
      //     [
      //       'Campaign Organiser',
      //       'Lead Generation Officer',
      //       'Facebook Poster',
      //       'Manager of Ads'
      //     ],
      //     3
      //   ),
      //   teams: chance.pickset(greekAlphabet, 3)
      // },
      // {
      //   name: 'PR',
      //   titles: chance.pickset(
      //     [
      //       'Community Manager',
      //       'Public Relations Specialist',
      //       'Manager of Damage Control'
      //     ],
      //     2
      //   ),
      //   teams: chance.pickset(greekAlphabet, 3)
      // }
    ],
    3
  );

const toIdentifier = (s: string) => {
  return romanize(s).toLowerCase().replace(/\s+/g, '');
};

const emailFrom = (firstName: string, lastName: string, domain: string) => {
  return toIdentifier(firstName) + '.' + toIdentifier(lastName) + '@' + domain;
};

const makeEmailsUnique = (users: GeneratedUser[]) => {
  const emailCounts: { [email: string]: number } = {};
  users.forEach((user) => {
    if (emailCounts[user.email] === undefined) {
      emailCounts[user.email] = 0;
    }

    emailCounts[user.email] += 1;
    if (emailCounts[user.email] > 1) {
      user.email =
        user.email.slice(0, -5) +
        emailCounts[user.email] +
        user.email.slice(-5);
    }
  });
  return users;
};

const divideUnequally = (total: number, divisions: number) => {
  const weights = _.map(
    (_) => chance.integer({ min: 1, max: 10 }),
    _.range(0, divisions)
  );
  const weightSum = _.sum(weights);
  return _.map((weight) => Math.round((weight / weightSum) * total), weights);
};

type GeneratorOptions = Partial<{
  hireDate: {
    min: Date;
    max: Date;
  };
  departments: Department[];
  cities: string[];
  domain: string;
}>;

export const getBiweeklyMonday = (x: moment.Moment): moment.Moment => {
  const m = x.clone();
  m.isoWeekday(1).startOf('d').hour(9);
  if (m.isoWeek() % 2 !== moment().isoWeek() % 2) {
    m.add(1, 'week');
  }
  return m;
};

export class Generator {
  hireDate: {
    min: Date;
    max: Date;
  };
  departments: Department[];
  cities: string[];
  domain: string;

  constructor(options: GeneratorOptions = {}) {
    this.hireDate = options.hireDate || {
      min: new Date(Date.now() - ONE_YEAR * 1),
      max: new Date(Date.now() + ONE_DAY * 21)
    };
    this.departments = options.departments || departments();
    this.cities = options.cities || cities();
    this.domain = options.domain || companyDomain();
  }

  private generateUser(manager: string): GeneratedUser {
    const gender = chance.pickone(['male', 'female']) as 'male' | 'female';
    const firstName = chance.first({ gender: gender });
    const lastName = chance.last();
    const department = chance.pickone(this.departments);
    return {
      firstName: firstName,
      lastName: lastName,
      gender: gender,
      email: emailFrom(firstName, lastName, this.domain),
      managerEmail: manager,
      department: department.name,
      position: chance.pickone(department.titles),
      team: chance.pickone(department.teams),
      city: chance.pickone(this.cities),
      employeeId: chance.natural(),
      hireDate: getBiweeklyMonday(
        moment(
          chance.date({
            string: true,
            min: this.hireDate.min,
            max: this.hireDate.max
          }),
          'M/D/YYYY'
        )
      ).format('M/D/YYYY')
    };
  }

  private generateManagers(numberOfManagers: number) {
    return _.map((_) => this.generateUser(''), _.range(0, numberOfManagers));
  }

  private generateReportsFromManagers(
    managers: GeneratedUser[],
    numberOfReports: number
  ) {
    return _.map(
      (_) => this.generateUser(chance.pickone(managers).email),
      _.range(0, numberOfReports)
    );
  }

  private generateTeam(
    size: number,
    manager: string,
    department: Department
  ): GeneratedUser[] {
    if (size < 3) {
      return [];
    }
    const teamSize = chance.integer({ min: 1, max: 10 });
    if (teamSize > size) {
      return _.map((_) => this.generateUser(manager), _.range(0, teamSize));
    }
    const reportCounts = divideUnequally(size - teamSize, teamSize);
    const usersWithCounts = _.map(
      (count) => ({ user: this.generateUser(manager), reportCount: count }),
      reportCounts
    );
    const reports = _.flatMap(
      (user) =>
        this.generateTeam(user.reportCount, user.user.email, department),
      usersWithCounts
    );
    return _.map('user', usersWithCounts).concat(reports);
  }

  async generateManagersAndReports(
    numberOfManagers: number,
    numberOfReports: number
  ) {
    const managers = this.generateManagers(numberOfManagers);
    const reports = this.generateReportsFromManagers(managers, numberOfReports);
    return makeEmailsUnique(managers.concat(reports));
  }

  async generateHierarchy(numberOfUsers: number): Promise<GeneratedUser[]> {
    const departments = this.departments;
    const generatedDepartments = chance.pickset(
      departments,
      chance.integer({ min: 3, max: departments.length })
    );
    const departmentSizes = divideUnequally(
      numberOfUsers,
      generatedDepartments.length
    );
    const departmentsWithSizes = _.zip(generatedDepartments, departmentSizes);
    return makeEmailsUnique(
      _.compact(
        _.flatMap(([department, size]) => {
          if (!size || !department) {
            return null;
          }
          return this.generateTeam(size, '', department);
        }, departmentsWithSizes)
      )
    );
  }
}
